/**
 * Special Events Permit Discovery — Block party, farmers market, street closure
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Stack,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack as BackIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import FeeDisplay from '../../components/permit/FeeDisplay';

const EVENT_TYPES = [
  'Block party',
  'Farmers market',
  'Street closure',
  'Outdoor festival',
  'Parade',
  'Film shoot',
  'Other event',
];

const SCOPES = {
  small: 'Under 50 people',
  medium: '50–200 people',
  large: '200–500 people',
  xlarge: 'Over 500 people',
};

const getPermitSuggestions = (eventType, location, scope) => {
  const permits = [];
  permits.push({ name: 'Event permit', desc: 'General event approval', typical: '2–6 weeks' });
  if (eventType?.toLowerCase().includes('block') || eventType?.toLowerCase().includes('street')) {
    permits.push({ name: 'Street closure permit', desc: 'Street/road closure', typical: '4–8 weeks' });
  }
  if (eventType?.toLowerCase().includes('farmers') || eventType?.toLowerCase().includes('market')) {
    permits.push({ name: 'Health department permit', desc: 'Food sales compliance', typical: '2–4 weeks' });
  }
  if (eventType?.toLowerCase().includes('festival') || eventType?.toLowerCase().includes('parade')) {
    permits.push({ name: 'Fire safety permit', desc: 'Crowd and fire safety', typical: '2–4 weeks' });
  }
  return permits;
};

export default function EventsPermitDemo({ onBack }) {
  const [step, setStep] = useState(0);
  const [eventType, setEventType] = useState('');
  const [location, setLocation] = useState('');
  const [scope, setScope] = useState('');

  const permits = getPermitSuggestions(eventType, location, scope);
  const steps = ['Event type', 'Location & date', 'Expected attendance'];
  const canProceed = step === 0 ? eventType : step === 1 ? location : scope;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onBack && (
          <Button size="small" startIcon={<BackIcon />} onClick={onBack}>Back</Button>
        )}
        <Typography variant="h6" sx={{ flex: 1 }}>Special Events Permits</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Block parties, farmers markets, festivals — we&apos;ll suggest which permits you need.
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {step === 0 && (
        <FormControl fullWidth>
          <InputLabel>What type of event?</InputLabel>
          <Select value={eventType} label="What type of event?" onChange={(e) => setEventType(e.target.value)}>
            {EVENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {step === 1 && (
        <TextField fullWidth label="Location and date" placeholder="e.g. Main St, June 15" value={location} onChange={(e) => setLocation(e.target.value)} />
      )}
      {step === 2 && (
        <FormControl fullWidth>
          <InputLabel>Expected attendance</InputLabel>
          <Select value={scope} label="Expected attendance" onChange={(e) => setScope(e.target.value)}>
            {Object.entries(SCOPES).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {step < 2 ? (
        <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!canProceed}>Next</Button>
      ) : (
        <Box>
          <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
            Based on your inputs, here are the permits you may need.
          </Alert>
          <Stack spacing={1}>
            {permits.map((p, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>{p.name}</Typography>
                <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
                <Chip label={`Typical: ${p.typical}`} size="small" sx={{ mt: 1 }} />
              </Paper>
            ))}
          </Stack>
          <FeeDisplay permitType="event-permit" scope={scope} />
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => { setStep(0); setEventType(''); setLocation(''); setScope(''); }}>
            Start over
          </Button>
        </Box>
      )}
    </Stack>
  );
}
